import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box, Button, Container, TextField,
  Typography, Alert, Paper
} from "@mui/material";
import { AuthAdminContext } from "../../clients/utils/AuthAdminContext";
import Header from "../../components/Header";

export default function LoginAdmin() {
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [error, setError]   = useState(null);
  const [view, setView]     = useState("login"); // "login" | "forgot" | "forgot_sent"
  const [username, setUsername] = useState("");
  const [forgotMsg, setForgotMsg] = useState(null);
  const [forgotErr, setForgotErr] = useState(null);
  const [forgotLoading, setForgotLoading] = useState(false);

  const navigate = useNavigate();
  const { dispatch } = useContext(AuthAdminContext);

  const handleChange = (e) => {
    setCredentials({ ...credentials, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    dispatch({ type: "ADMIN_LOGIN_START" });
    try {
      const res = await axios.post(
        "https://api.komorotel.com/api/authAdmin/login",
        { username: credentials.username, password: credentials.password },
        { withCredentials: true }
      );
      dispatch({ type: "ADMIN_LOGIN_SUCCESS", payload: res.data });

      if (res.data.isBoss) {
        navigate(`/devAdmin/${res.data._id}`);
      } else if (res.data.hotelId) {
        navigate(`/admin/${res.data._id}/${res.data.hotelId}`);
      } else {
        navigate(`/admin/${res.data._id}`);
      }
    } catch (err) {
      setError(err.response?.data);
      dispatch({ type: "ADMIN_LOGIN_ERROR", payload: err.response?.data });
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotErr(null);
    setForgotLoading(true);
    try {
      await axios.post("https://api.komorotel.com/api/authAdmin/forgot-password", { username });
      setView("forgot_sent");
    } catch (err) {
      setForgotErr(err.response?.data?.message || "Erreur — vérifiez votre nom d'utilisateur.");
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <Box className="w-100 h-100">
      <Header />
      <Container maxWidth="sm" sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 28 }}>

        {/* Logo */}
        <Typography variant="h4" component="h1" align="center" gutterBottom>
          <span style={{ background: "#3A9D23", color: "#fff", padding: "0.5rem 1rem", borderRadius: "8px" }}>
            Komorotel
          </span>
        </Typography>

        <Paper elevation={3} sx={{ p: 4, width: "100%", mt: 4 }}>

          {/* ── VUE LOGIN ───────────────────────────────────────── */}
          {view === "login" && (
            <>
              <Typography variant="h5" align="center" gutterBottom>
                Connexion Administrateur
              </Typography>

              <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 2 }}>
                <TextField
                  id="username"
                  label="Nom d'utilisateur"
                  fullWidth
                  margin="normal"
                  onChange={handleChange}
                  required
                />
                <TextField
                  id="password"
                  label="Mot de passe"
                  type="password"
                  fullWidth
                  margin="normal"
                  onChange={handleChange}
                  required
                />

                {error && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {error.message || "Une erreur est survenue."}
                  </Alert>
                )}

                <Button type="submit" fullWidth variant="contained" color="primary" sx={{ mt: 3, py: 1.5 }}>
                  Confirmer
                </Button>

                {/* Lien mot de passe oublié */}
                <Box sx={{ textAlign: "center", mt: 2 }}>
                  <button
                    type="button"
                    onClick={() => setView("forgot")}
                    style={{ background: "none", border: "none", color: "#3A9D23", cursor: "pointer", fontSize: 13, textDecoration: "underline" }}
                  >
                    Mot de passe oublié ?
                  </button>
                </Box>
              </Box>
            </>
          )}

          {/* ── VUE FORGOT ──────────────────────────────────────── */}
          {view === "forgot" && (
            <>
              <Typography variant="h5" align="center" gutterBottom>
                Mot de passe oublié
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                Entrez votre nom d'utilisateur — un lien de réinitialisation sera envoyé à l'email associé.
              </Typography>

              <Box component="form" onSubmit={handleForgot} noValidate>
                <TextField
                  label="Nom d'utilisateur"
                  fullWidth
                  margin="normal"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />

                {forgotErr && (
                  <Alert severity="error" sx={{ mt: 1 }}>{forgotErr}</Alert>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  sx={{ mt: 3, py: 1.5 }}
                  disabled={forgotLoading}
                >
                  {forgotLoading ? "Envoi en cours..." : "Envoyer le lien"}
                </Button>

                <Box sx={{ textAlign: "center", mt: 2 }}>
                  <button
                    type="button"
                    onClick={() => setView("login")}
                    style={{ background: "none", border: "none", color: "#888", cursor: "pointer", fontSize: 13, textDecoration: "underline" }}
                  >
                    ← Retour à la connexion
                  </button>
                </Box>
              </Box>
            </>
          )}

          {/* ── VUE CONFIRMATION ────────────────────────────────── */}
          {view === "forgot_sent" && (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ color: "#3A9D23" }}>
                ✓ Email envoyé
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Si un compte existe avec ce nom d'utilisateur, vous recevrez un lien de réinitialisation sous peu.
              </Typography>
              <button
                type="button"
                onClick={() => setView("login")}
                style={{ background: "none", border: "none", color: "#3A9D23", cursor: "pointer", fontSize: 13, textDecoration: "underline" }}
              >
                ← Retour à la connexion
              </button>
            </Box>
          )}

        </Paper>
      </Container>
    </Box>
  );
}